import type {
  ConditionExpr,
  ConditionPredicate,
  FormFieldConditionalRuleRow,
  FormFieldDefinitionRow,
} from '@/api/client';

export interface ClientEvalContext {
  defs: FormFieldDefinitionRow[];
  valuesByFieldId: Record<string, unknown>;
  subjectAttrs: Record<string, unknown>;
  studyAttrs: Record<string, unknown>;
}

function isEmpty(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (typeof v === 'string' && v === '') return true;
  if (Array.isArray(v) && v.length === 0) return true;
  return false;
}

// Mirrors services/forms/conditional.ts::looseEquals on the backend —
// a saved rule value's casing can't be trusted to match the real stored
// data (e.g. "male" vs "Male"), so string equality here is
// case/whitespace-insensitive; every other type keeps exact `===`.
function looseEquals(a: unknown, b: unknown): boolean {
  if (typeof a === 'string' && typeof b === 'string') {
    return a.trim().toLowerCase() === b.trim().toLowerCase();
  }
  return a === b;
}

function resolveFieldRef(ref: string, ctx: ClientEvalContext): unknown {
  if (ref.startsWith('subject.')) return ctx.subjectAttrs[ref.slice('subject.'.length)];
  if (ref.startsWith('study.')) return ctx.studyAttrs[ref.slice('study.'.length)];
  const def = ctx.defs.find((d) => d.field_key === ref);
  if (!def) return undefined;
  return ctx.valuesByFieldId[def.field_id];
}

function evalPredicate(pred: ConditionPredicate, ctx: ClientEvalContext): boolean | null {
  const left = resolveFieldRef(pred.field, ctx);
  if (pred.op === 'empty') return isEmpty(left);
  if (pred.op === 'exists') return !isEmpty(left);
  if (left === undefined || left === null) return null;
  const right = pred.value;

  switch (pred.op) {
    case 'eq':
      return looseEquals(left, right);
    case 'neq':
      return !looseEquals(left, right);
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte': {
      const l = typeof left === 'number' ? left : Number(left);
      const r = typeof right === 'number' ? right : Number(right);
      if (!Number.isFinite(l) || !Number.isFinite(r)) return null;
      if (pred.op === 'gt') return l > r;
      if (pred.op === 'gte') return l >= r;
      if (pred.op === 'lt') return l < r;
      return l <= r;
    }
    case 'in':
      if (Array.isArray(right)) return right.some((r) => looseEquals(left, r));
      return null;
    case 'notin':
      if (Array.isArray(right)) return !right.some((r) => looseEquals(left, r));
      return null;
    case 'between': {
      if (!Array.isArray(right) || right.length !== 2) return null;
      const l = typeof left === 'number' ? left : Number(left);
      const lo = Number(right[0]);
      const hi = Number(right[1]);
      if (!Number.isFinite(l) || !Number.isFinite(lo) || !Number.isFinite(hi)) return null;
      return l >= lo && l <= hi;
    }
    default:
      return null;
  }
}

export function evaluateExpr(expr: ConditionExpr | null, ctx: ClientEvalContext): boolean | null {
  if (!expr) return null;
  if ('all' in expr && Array.isArray(expr.all)) {
    let sawUncertain = false;
    for (const child of expr.all) {
      const r = evaluateExpr(child, ctx);
      if (r === false) return false;
      if (r === null) sawUncertain = true;
    }
    return sawUncertain ? null : true;
  }
  if ('any' in expr && Array.isArray(expr.any)) {
    let sawUncertain = false;
    for (const child of expr.any) {
      const r = evaluateExpr(child, ctx);
      if (r === true) return true;
      if (r === null) sawUncertain = true;
    }
    return sawUncertain ? null : false;
  }
  if ('op' in expr && 'field' in expr) {
    return evalPredicate(expr as ConditionPredicate, ctx);
  }
  return null;
}

export function computeHiddenFieldIds(
  rules: FormFieldConditionalRuleRow[],
  ctx: ClientEvalContext,
): Set<string> {
  const hidden = new Set<string>();
  for (const rule of rules) {
    let expr: ConditionExpr | null = null;
    try {
      const parsed = JSON.parse(rule.condition_expr);
      expr = parsed && typeof parsed === 'object' ? (parsed as ConditionExpr) : null;
    } catch {
      expr = null;
    }
    const verdict = evaluateExpr(expr, ctx);
    if (verdict === null) continue;
    if (rule.action === 'show' && verdict === false) hidden.add(rule.field_id);
    if (rule.action === 'hide' && verdict === true) hidden.add(rule.field_id);
  }
  return hidden;
}
